import React, { useState, useRef } from 'react';
import { Upload, FileText, X, CheckCircle, Camera } from 'lucide-react';
import { useLanguage } from 'hooks/useLanguage';
import Button from 'components/ui/Button';

const VerificationStep = ({ formData, onInputChange, error }) => {
  const { t } = useLanguage();
  const idDocumentRef = useRef(null);
  const proofOfAddressRef = useRef(null);
  
  const [dragOver, setDragOver] = useState({ id: false, address: false });

  const handleFileSelect = (type, file) => {
    if (file && validateFile(file)) {
      onInputChange(type, file);
    }
  };

  const validateFile = (file) => {
    // Max file size: 10MB
    const maxSize = 10 * 1024 * 1024;
    if (file?.size > maxSize) {
      alert(t('File size must be less than 10MB'));
      return false;
    }

    // Allowed file types
    const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'application/pdf'];
    if (!allowedTypes?.includes(file?.type)) {
      alert(t('Please upload only JPEG, PNG, or PDF files'));
      return false;
    }

    return true;
  };

  const handleDrop = (e, type) => {
    e?.preventDefault();
    setDragOver(prev => ({ ...prev, [type]: false }));
    
    const files = e?.dataTransfer?.files;
    if (files && files?.[0]) {
      handleFileSelect(type === 'id' ? 'idDocument' : 'proofOfAddress', files?.[0]);
    }
  };

  const handleDragOver = (e, type) => {
    e?.preventDefault();
    setDragOver(prev => ({ ...prev, [type]: true }));
  };

  const handleDragLeave = (e, type) => {
    e?.preventDefault();
    setDragOver(prev => ({ ...prev, [type]: false }));
  };

  const removeFile = (type) => {
    onInputChange(type, null);
  };

  const FileUploadArea = ({ type, file, label, description, acceptedFormats }) => {
    const isDragOver = type === 'id' ? dragOver?.id : dragOver?.address;
    const fileRef = type === 'id' ? idDocumentRef : proofOfAddressRef;
    const fieldName = type === 'id' ? 'idDocument' : 'proofOfAddress';

    return (
      <div className="space-y-2">
        <label className="block text-sm font-medium text-gray-700">
          {label} *
        </label>
        <div
          className={`relative border-2 border-dashed rounded-lg p-6 text-center transition-colors ${
            isDragOver
              ? 'border-blue-400 bg-blue-50'
              : file
              ? 'border-green-400 bg-green-50' :'border-gray-300 hover:border-gray-400'
          }`}
          onDrop={(e) => handleDrop(e, type)}
          onDragOver={(e) => handleDragOver(e, type)}
          onDragLeave={(e) => handleDragLeave(e, type)}
        >
          <input
            ref={fileRef}
            type="file"
            accept=".jpg,.jpeg,.png,.pdf"
            onChange={(e) => handleFileSelect(fieldName, e?.target?.files?.[0])}
            className="hidden"
          />

          {file ? (
            <div className="space-y-3">
              <div className="flex items-center justify-center">
                <CheckCircle className="w-8 h-8 text-green-500" />
              </div>
              <div>
                <p className="font-medium text-green-700">{file?.name}</p>
                <p className="text-sm text-green-600">
                  {(file?.size / 1024 / 1024)?.toFixed(2)} MB
                </p>
              </div>
              <Button
                type="button"
                onClick={() => removeFile(fieldName)}
                variant="outline"
                className="text-red-600 border-red-300 hover:bg-red-50"
              >
                <X className="w-4 h-4 mr-2" />
                {t('Remove')}
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              <div className="flex items-center justify-center">
                <Upload className="w-8 h-8 text-gray-400" />
              </div>
              <div>
                <p className="text-gray-600 font-medium">
                  {t('Drop your file here or')}
                </p>
                <Button
                  type="button"
                  onClick={() => fileRef?.current?.click()}
                  variant="outline"
                  className="mt-2"
                >
                  {t('Choose File')}
                </Button>
              </div>
            </div>
          )}

          {/* Camera option for mobile */}
          <div className="mt-3 pt-3 border-t border-gray-200">
            <Button
              type="button"
              onClick={() => {
                const input = document.createElement('input');
                input.type = 'file';
                input.accept = 'image/*';
                input.capture = 'environment';
                input.onchange = (e) => handleFileSelect(fieldName, e?.target?.files?.[0]);
                input?.click();
              }}
              variant="outline"
              className="text-blue-600 border-blue-300 hover:bg-blue-50"
            >
              <Camera className="w-4 h-4 mr-2" />
              {t('Take Photo')}
            </Button>
          </div>
        </div>
        <div className="text-xs text-gray-500 space-y-1">
          <p>{description}</p>
          <p>{t('Accepted formats')}: {acceptedFormats}</p>
          <p>{t('Maximum file size')}: 10MB</p>
        </div>
      </div>
    );
  };

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">
          {t('Document Verification')}
        </h2>
        <p className="text-gray-600">
          {t('Upload required documents to verify your identity and complete your account setup')}
        </p>
      </div>
      {/* KYC Information */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <FileText className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium text-blue-800 mb-2">
              {t('Know Your Customer (KYC)')}
            </h3>
            <p className="text-sm text-blue-700 mb-2">
              {t('We are required by law to verify your identity. This helps protect your account and comply with regulations.')}
            </p>
            <ul className="text-sm text-blue-700 space-y-1">
              <li>• {t('Documents must be clear and readable')}</li>
              <li>• {t('All four corners must be visible')}</li>
              <li>• {t('No screenshots or photocopies')}</li>
              <li>• {t('Documents must be current and valid')}</li>
            </ul>
          </div>
        </div>
      </div>
      {/* ID Document Upload */}
      <FileUploadArea
        type="id"
        file={formData?.idDocument}
        label={t('Government-Issued ID')}
        description={t('Upload a photo of your passport, driver\'s license, or national ID card')}
        acceptedFormats="JPEG, PNG, PDF"
      />
      {/* Proof of Address Upload */}
      <FileUploadArea
        type="address"
        file={formData?.proofOfAddress}
        label={t('Proof of Address')}
        description={t('Upload a recent utility bill, bank statement, or official document showing your address (not older than 3 months)')}
        acceptedFormats="JPEG, PNG, PDF"
      />
      {/* Verification Process Info */}
      <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
        <h3 className="font-medium text-gray-900 mb-2">
          {t('What happens next?')}
        </h3>
        <div className="space-y-2 text-sm text-gray-700">
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
            <span>{t('Your documents will be reviewed within 24-48 hours')}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
            <span>{t('You will receive an email confirmation once approved')}</span>
          </div>
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-blue-500 rounded-full"></div>
            <span>{t('You can start trading immediately after verification')}</span>
          </div>
        </div>
      </div>
      {/* Security Notice */}
      <div className="bg-green-50 border border-green-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          <div>
            <h3 className="font-medium text-green-800 mb-1">
              {t('Your documents are secure')}
            </h3>
            <p className="text-sm text-green-700">
              {t('All uploaded documents are encrypted and stored securely. We never share your personal information with third parties without your consent.')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default VerificationStep;